import {
  ComponentMirror, PendingTasks, inject, Injectable, InjectionToken, Provider, reflectComponentType, Type,
} from '@angular/core'

export type LazyComponentDeclaration = [
  name: string,
  selector: string,
  componentType: () => Promise<Type<unknown>>,
]
export type ComponentDeclaration = [
  name: string,
  componentType: Type<unknown>,
]

export interface ComponentFactoryServiceConfiguration {
  components: ComponentDeclaration[]
  lazyComponents: LazyComponentDeclaration[]
}

const COMPONENT_FACTORY_SERVICE_CONFIGURATION_TOKEN = new InjectionToken<ComponentFactoryServiceConfiguration>('kb:component-factory-service-configuration')

export function provideComponentFactoryConfiguration(configuration: ComponentFactoryServiceConfiguration): Provider {
  return {
    provide: COMPONENT_FACTORY_SERVICE_CONFIGURATION_TOKEN,
    useValue: configuration,
  }
}

@Injectable({
  providedIn: 'any', // This is needed so that each feature module can declare it's own set of configured components
})
export class ComponentFactoryService {
  private configuration = inject(COMPONENT_FACTORY_SERVICE_CONFIGURATION_TOKEN)
  private componentMirrors = new Map<string, ComponentMirror<unknown>>()
  private taskService = inject(PendingTasks)

  public constructor() {
    for (const [name, componentType] of this.configuration.components) {
      const componentMirror = reflectComponentType(componentType)
      if (componentMirror === null) {
        continue
      }

      this.componentMirrors.set(name, componentMirror)
    }
  }

  public async getComponentMirrorByName<T>(name: string): Promise<ComponentMirror<T> | null> {
    if (this.componentMirrors.has(name)) {
      return Promise.resolve(this.componentMirrors.get(name) as ComponentMirror<T>)
    }

    const componentType = this.configuration.lazyComponents.find(component => component[0] === name)
    if (componentType === undefined) {
      return null
    }

    const task = this.taskService.add()
    const componentMirror = await componentType[2]() as Type<T>
    task()

    return reflectComponentType(componentMirror)
  }

  public async getComponentMirrorByElement<T>(element: Element): Promise<ComponentMirror<T> | null> {
    for (const componentMirror of this.componentMirrors.values()) {
      if (element.matches(componentMirror.selector)) {
        return componentMirror as ComponentMirror<T>
      }
    }
    for (const [, selector, componentType] of this.configuration.lazyComponents) {
      if (element.matches(selector)) {
        const task = this.taskService.add()
        const componentMirror = await componentType() as Type<T>
        task()

        return reflectComponentType(componentMirror)
      }
    }
    return null
  }
}
